import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Textarea } from '@scio3/ui';
import { ScreenCountSelector } from '../../components/ScreenCountSelector';

type Props = {
  screenCount: number;
  onScreenCountChange: (value: number) => void;
  minScreens: number;
  /** For trial users: "Your Plan: Pro Plus $8.33/screen/month" */
  planInfoText: string | undefined;
  /** Whether user is on a paid (non-trial) plan */
  isPaid: boolean;
  /** Plan name for paid users (e.g. "Pro Plus") */
  planName?: string;
  /** Price text for paid users (e.g. "$162.00/screen/per year") */
  planPriceText?: string;
  /** Called when paid user clicks "(change)" to go to plan selection */
  onChangePlan?: () => void;
  onDecreaseBlocked: () => void;

  /**
   * Team-billing mode only (Issue #49 — scio1 parity). When `teamMode` is
   * true the step renders Team Name + Purpose inputs below the screen counter
   * so admins can rename the team / edit its description while updating the
   * subscription in a single flow.
   */
  teamMode?: boolean;
  teamName?: string;
  onTeamNameChange?: (value: string) => void;
  teamDescription?: string;
  onTeamDescriptionChange?: (value: string) => void;
};

/**
 * Step 1: Number of Screens
 * - Trial: shows plan info text below spinner
 * - Paid: shows "Your Plan: [name] [price] (change)" with link to plan step
 * - Team billing: additionally shows Team Name + Purpose inputs
 */
export function WizardStepScreens({
  screenCount,
  onScreenCountChange,
  minScreens,
  planInfoText,
  isPaid,
  planName,
  planPriceText,
  onChangePlan,
  onDecreaseBlocked,
  teamMode,
  teamName,
  onTeamNameChange,
  teamDescription,
  onTeamDescriptionChange,
}: Props) {
  const { t } = useTranslation();

  // For paid users, build the plan info with "(change)" link
  const paidPlanInfo =
    isPaid && planName ? (
      <span>
        {t('Your Plan')}: {planName} {planPriceText}
        {onChangePlan && (
          <>
            {' '}
            (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="text-primary cursor-pointer h-auto p-0"
              onClick={onChangePlan}
            >
              {t('change')}
            </Button>
            )
          </>
        )}
      </span>
    ) : undefined;

  return (
    <div className="flex flex-col items-center py-6">
      <h1 className="text-xl font-semibold text-center mb-8">{t('Number of Screens')}</h1>
      <ScreenCountSelector
        value={screenCount}
        onChange={onScreenCountChange}
        min={minScreens}
        planInfoText={isPaid ? undefined : planInfoText}
        onDecreaseBlocked={onDecreaseBlocked}
      />
      {paidPlanInfo && (
        <p className="text-sm text-muted-foreground text-center mt-4">{paidPlanInfo}</p>
      )}

      {/* Team-billing inputs — scio1 parity (Issue #49) */}
      {teamMode && (
        <div className="mt-8 w-full max-w-md space-y-4 px-4">
          <div className="space-y-2">
            <Label htmlFor="wizard-team-name">{t('Team Name')}</Label>
            <Input
              id="wizard-team-name"
              value={teamName ?? ''}
              onChange={(e) => onTeamNameChange?.(e.target.value)}
              placeholder={t('Team Name')}
              maxLength={64}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wizard-team-description">{t('Purpose')}</Label>
            <Textarea
              id="wizard-team-description"
              value={teamDescription ?? ''}
              onChange={(e) => onTeamDescriptionChange?.(e.target.value)}
              placeholder={t('Purpose')}
              maxLength={256}
              rows={3}
            />
          </div>
        </div>
      )}
    </div>
  );
}
